/**
 * Biofield — Phase 2's animated background, rendered as a world-anchored
 * Mesh that lives inside worldContainer. The shader samples in *local*
 * (= world) coordinates via vWorldPos, so camera scrolling is handled
 * entirely by the parent container's transform — no uniform tracks the
 * camera, no offscreen filter target resamples the world. That means
 * scrolling never blurs or shears the field, and entities sitting on
 * top of it never read as "moving relative to the bg".
 *
 * Layers (all anchored in world space):
 *   1. Vertical depth gradient (world-Y based)
 *   2. Domain-warped FBM current
 *   3. Ridged-FBM capillary veins
 *   4. Sparse drift spores
 *   5. Five bloom nebulae anchored at fixed world fractions
 *   6. Caustic sweep + cluster bioluminescent halo
 *
 * No vignette — the bg lives inside the world now, so screen-space
 * framing isn't this layer's job.
 */

import { Mesh, MeshGeometry, Shader, GlProgram, Texture } from 'pixi.js';

const vertex = /* glsl */ `
  in vec2 aPosition;

  uniform mat3 uProjectionMatrix;
  uniform mat3 uWorldTransformMatrix;
  uniform mat3 uTransformMatrix;

  out vec2 vWorldPos;

  void main() {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    // aPosition is the mesh's local coord, and the mesh sits at (0,0)
    // inside worldContainer with no local rotation/scale — so the local
    // coord IS the world coord. Pass it through to the fragment so the
    // pattern is anchored to fixed world points and never drifts when
    // the camera scrolls.
    vWorldPos = aPosition;
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  in vec2 vWorldPos;
  out vec4 finalColor;

  uniform float uTime;
  uniform vec2 uWorldSize;
  uniform vec2 uClusterPos;

  float hash1(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  float valueNoise(vec2 uv) {
    vec2 i = floor(uv);
    vec2 f = fract(uv);
    float a = hash1(i);
    float b = hash1(i + vec2(1.0, 0.0));
    float c = hash1(i + vec2(0.0, 1.0));
    float d = hash1(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  float fbm(vec2 uv) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      v += valueNoise(uv) * amp;
      uv *= 2.02;
      amp *= 0.5;
    }
    return v;
  }

  float ridge(float x) {
    return 1.0 - abs(2.0 * x - 1.0);
  }

  void main() {
    vec2 wp = vWorldPos;
    float t = uTime;

    float worldY = clamp(wp.y / max(uWorldSize.y, 1.0), 0.0, 1.0);
    vec3 topCol = vec3(0.060, 0.030, 0.110);
    vec3 botCol = vec3(0.018, 0.008, 0.045);
    vec3 col = mix(topCol, botCol, smoothstep(0.0, 1.0, worldY));

    vec2 wuv = wp / 380.0;

    vec2 warp = vec2(
      fbm(wuv * 0.55 + vec2(t * 0.040, 0.0)),
      fbm(wuv * 0.55 + vec2(7.3, t * 0.034))
    ) - 0.5;
    vec2 fuv = wuv + warp * 1.4;

    float r1 = fbm(fuv * 1.0);
    float r2 = fbm(fuv * 2.2 + vec2(0.0, t * 0.10));
    float veins = ridge(r1) * ridge(r2);
    veins = smoothstep(0.52, 0.95, veins);
    vec3 veinCol = vec3(0.55, 0.32, 0.85);
    col += veinCol * veins * 0.32;

    vec2 sp = wuv * 5.5 + vec2(t * 0.09, t * 0.06);
    float spores = valueNoise(sp) * 0.55 + valueNoise(sp * 1.7 + 4.1) * 0.45;
    float sporeMask = smoothstep(0.78, 0.86, spores);
    col += vec3(0.95, 0.85, 1.00) * sporeMask * 0.50;

    float sweep = sin(wp.x * 0.0035 - t * 0.55 + sin(wp.y * 0.0018 + t * 0.12) * 1.8);
    float sweepBand = smoothstep(0.55, 0.92, sweep);
    float topMask = smoothstep(0.85, 0.0, worldY);
    col += vec3(0.85, 0.72, 1.00) * sweepBand * topMask * 0.16;

    vec2 c1 = vec2(0.20, 0.28) * uWorldSize;
    vec2 c2 = vec2(0.74, 0.42) * uWorldSize;
    vec2 c3 = vec2(0.42, 0.78) * uWorldSize;
    vec2 c4 = vec2(0.85, 0.15) * uWorldSize;
    vec2 c5 = vec2(0.12, 0.82) * uWorldSize;
    float d1 = distance(wp, c1);
    float d2 = distance(wp, c2);
    float d3 = distance(wp, c3);
    float d4 = distance(wp, c4);
    float d5 = distance(wp, c5);
    float p1 = 0.55 + 0.45 * sin(t * 0.40);
    float p2 = 0.55 + 0.45 * sin(t * 0.33 + 1.4);
    float p3 = 0.55 + 0.45 * sin(t * 0.37 + 2.7);
    float p4 = 0.55 + 0.45 * sin(t * 0.29 + 4.1);
    float p5 = 0.55 + 0.45 * sin(t * 0.42 + 5.6);
    col += vec3(0.62, 0.38, 0.92) * exp(-d1 / 720.0) * p1 * 0.34;
    col += vec3(0.92, 0.45, 0.78) * exp(-d2 / 820.0) * p2 * 0.30;
    col += vec3(0.42, 0.28, 0.95) * exp(-d3 / 760.0) * p3 * 0.28;
    col += vec3(0.55, 0.85, 0.95) * exp(-d4 / 700.0) * p4 * 0.26;
    col += vec3(0.95, 0.65, 0.50) * exp(-d5 / 780.0) * p5 * 0.24;

    float dCluster = distance(wp, uClusterPos);
    float clusterPulse = 0.65 + 0.35 * sin(t * 1.1);
    col += vec3(0.78, 0.55, 1.00) * exp(-dCluster / 240.0) * clusterPulse * 0.22;

    finalColor = vec4(col, 1.0);
  }
`;

export class BiofieldMesh extends Mesh {
  constructor(worldW: number, worldH: number) {
    const positions = new Float32Array([
      0,      0,
      worldW, 0,
      worldW, worldH,
      0,      worldH,
    ]);
    const uvs = new Float32Array([
      0, 0,
      1, 0,
      1, 1,
      0, 1,
    ]);
    const indices = new Uint32Array([0, 1, 2, 0, 2, 3]);

    const geometry = new MeshGeometry({ positions, uvs, indices });

    const glProgram = GlProgram.from({ vertex, fragment, name: 'biofield-mesh' });
    const shader = new Shader({
      glProgram,
      resources: {
        uniforms: {
          uTime: { value: 0, type: 'f32' },
          uWorldSize: { value: [worldW, worldH], type: 'vec2<f32>' },
          uClusterPos: { value: [worldW * 0.5, worldH * 0.5], type: 'vec2<f32>' },
        },
      },
    });

    super({
      geometry,
      shader: shader as unknown as never,
      texture: Texture.WHITE,
    });
  }

  update(dt: number, clusterX: number, clusterY: number): void {
    const u = ((this.shader as Shader).resources.uniforms as any).uniforms;
    u.uTime += dt;
    u.uClusterPos[0] = clusterX;
    u.uClusterPos[1] = clusterY;
  }
}
